import { z } from 'zod';

export interface IIqGraphRatesStateService {
    getThirteenMonthForecastRates(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
        quote: Quote,
    ): Promise<Rate[]>;

    getThirteenMonthHistoricalRates(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
        quote: Quote,
    ): Promise<Rate[]>;

    getThirtyFiveDayForecastRates(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
        quote: Quote,
    ): Promise<Rate[]>;

    getContributions(
        origin: string,
        destination: string,
        equipmentType: EquipmentType,
        contractType: ContractType,
        perMile: boolean,
    ): Promise<Contribution[]>;
}
export enum ContractTypeEnum {
    Contract = 'CONTRACT',
    Spot = 'SPOT',
}

export enum EquipmentTypeEnum {
    Van = 'VAN',
    Flatbed = 'FLATBED',
    Reefer = 'REEFER',
}

export enum UserTypeEnum {
    Broker = 'Broker',
    Carrier = 'Carrier',
    Shipper = 'Shipper',
    Intermodal = 'Intermodal',
    ContractCarrier = 'ContractCarrier',
}

export enum BrokerPaymentTypeEnum {
    BCC = 'BCC',
    BCS = 'BCS',
}

export enum ShipperPaymentTypeEnum {
    // SCS = 'SCS',
    SCC = 'SCC',
    SBS = 'SBS',
    SBC = 'SBC',
}

export enum ContributionEscalationLevelEnum {
    POSTAL_AREA = 'POSTAL_AREA',
    MARKET_AREA = 'MARKET_AREA',
}

export enum GraphColors {
    Contract = '#1c60f2',
    Spot = '#ffbc04',
    BrokerCarrierSpot = '#ffbc04',
    BrokerCarrierSpotArea = '#ffbc04',
    ShipperCarrierContract = '#1c60f2',
    ShipperCarrierContractArea = '#1c60f2',

}

// Schemas
export const LocationSchema = z.object({
    city: z.string(),
    stateProv: z.string(),
    postalCode: z.string(),
});
export const equipmentTypeSchema = z.nativeEnum(EquipmentTypeEnum);
export const ContractTypesSchema = z.nativeEnum(ContractTypeEnum);
export const userTypeSchema = z.nativeEnum(UserTypeEnum);
export const BrokerPaymentTypeSchema = z.nativeEnum(BrokerPaymentTypeEnum);
export const ShipperPaymentTypeSchema = z.nativeEnum(ShipperPaymentTypeEnum);
export const ContributionEscalationLevelSchema = z.nativeEnum(
    ContributionEscalationLevelEnum,
);
export const QuoteSchema = z.object({
    fuel: z.number(),
});
export const RateSchema = z.object({
    average: z.number(),
    high: z.number(),
    low: z.number(),
    date: z.string(),
    escalationOrigin: z.string().optional(),
    escalationDestination: z.string().optional(),
});

export const BestFitRateSchema = z.object({
    average: z.number(),
    high: z.number(),
    low: z.number(),
    escalation: z.string(),
});
export const GraphQlContributionDataSchema = z.object({
    id: z.string(),
    pickupDate: z.string(),
    totalPaidByBroker: z.number(),
    totalPaidByShipper: z.number(),
    totalPaidByCustomer: z.number(),
    customerName: z.string(),
    shipperName: z.string(),
    brokerName: z.string(),
    carrierName: z.string(),
    brokerPaymentType: z.custom<BrokerPaymentType>(),
    shipperPaymentType: z.custom<ShipperPaymentType>(),
    contributorType: z.custom<UserType>(),
    equipType: z.custom<EquipmentType>(),
});
export const GraphQlContributionsSchema = z.object({
    data: z.object({
        getCustomerContributions: z.array(z.custom<GraphQlContributionData>()),
    }),
});
export const ContributionSchema = z.object({
    origin: z.custom<Location>(),
    destination: z.custom<Location>(),
    equipmentType: z.custom<EquipmentType>(),
    date: z.string(),
    userType: z.custom<UserType>(),
    rateType: z.custom<ContractType>(),
    value: z.number(),
    shipperName: z.string(),
});

const EscalationDataSchema = z.object({
    timeEscalation: z.string(),
    rates: z.object({
        trip: z.object({
            date: z.string(),
            average: z.number(),
            range: z.object({
                high: z.number(),
                low: z.number(),
            }),
        }),
        mile: z.object({
            date: z.string(),
            average: z.number(),
            range: z.object({
                high: z.number(),
                low: z.number(),
            }),
        }),
    }),
    timeInterval: z.string(),
    mileage: z.number(),
    reports: z.number(),
    companies: z.number(),
    escalationOrigin: z.string(),
    escalationDestination: z.string(),
});
export type EscalationData = z.infer<typeof EscalationDataSchema>;

export const GraphQlHistoricalSchema = z.object({
    rate: z.object({
        average: z.object({
            perTrip: z.object({
                value: z.number(),
            }),
            perMile: z.object({
                value: z.number(),
            }),
        }),
        high: z.object({
            perTrip: z.object({
                value: z.number(),
            }),
            perMile: z.object({
                value: z.number(),
            }),
        }),
        low: z.object({
            perTrip: z.object({
                value: z.number(),
            }),
            perMile: z.object({
                value: z.number(),
            }),
        }),
        mileage: z.number(),
        reports: z.number(),
        companies: z.number(),
        timeEscalation: z.string(),
        area: z.string(),
        areaDestination: z.string(),
        escalationMap: z.object({
            threedays: z.custom<EscalationData>(),
            sevendays: z.custom<EscalationData>(),
            fifteendays: z.custom<EscalationData>(),
            thirtydays: z.custom<EscalationData>(),
            sixtydays: z.custom<EscalationData>(),
            ninetydays: z.custom<EscalationData>(),
            onehundredeightydays: z.custom<EscalationData>(),
            oneyear: z.custom<EscalationData>(),
        }),
    }),
});
export type GraphQlHistorical = z.infer<typeof GraphQlHistoricalSchema>;

// Types
export type Contribution = Required<z.infer<typeof ContributionSchema>>;
export type Location = z.infer<typeof LocationSchema>;
export type EquipmentType = z.infer<typeof equipmentTypeSchema>;
export type Quote = z.infer<typeof QuoteSchema>;
export type ContractType = z.infer<typeof ContractTypesSchema>;
export type UserType = z.infer<typeof userTypeSchema>;
export type BrokerPaymentType = z.infer<typeof BrokerPaymentTypeSchema>;
export type ShipperPaymentType = z.infer<typeof ShipperPaymentTypeSchema>;
export type ContributionEscalationLevel = z.infer<
    typeof ContributionEscalationLevelSchema
>;
export type Rate = Required<z.infer<typeof RateSchema>>;
export type BestFitRate = Required<z.infer<typeof BestFitRateSchema>>;
export type GraphQlContributionData = z.infer<
    typeof GraphQlContributionDataSchema
>;
export type GraphQlContributions = z.infer<typeof GraphQlContributionsSchema>;

export const spotInfoLabelSvgPath =
    'M 91.2421 9.8682 Z Z M 91.274 12.4495 C 90.3815 12.4495 89.5422 12.2801 88.7572 11.9413 C 87.9722 11.6025 87.2887 11.1427 86.7079 10.5619 C 86.1271 9.9811 85.6673 9.2981 85.3285 8.513 C 84.9897 7.7278 84.8203 6.8889 84.8203 5.9962 C 84.8203 5.1034 84.9897 4.2645 85.3285 3.4794 C 85.6673 2.6942 86.1271 2.0112 86.7079 1.4304 C 87.2887 0.8496 87.9722 0.3898 88.7572 0.051 C 89.5422 -0.2878 90.3815 -0.4572 91.274 -0.4572 C 92.1665 -0.4572 93.0058 -0.2878 93.7908 0.051 C 94.5758 0.3898 95.2593 0.8496 95.8401 1.4304 C 96.4209 2.0112 96.8807 2.6942 97.2195 3.4794 C 97.5583 4.2645 97.7277 5.1034 97.7277 5.9962 C 97.7277 6.8889 97.5583 7.7278 97.2195 8.513 C 96.8807 9.2981 96.4209 9.9811 95.8401 10.5619 C 95.2593 11.1427 94.5758 11.6025 93.7908 11.9413 C 93.0058 12.2801 92.1665 12.4495 91.274 12.4495 Z Z M 91.4705 10.4025 C 91.6778 10.4025 91.8398 10.3348 91.9781 10.1967 C 92.1164 10.0583 92.1842 9.8963 92.1842 9.6889 C 92.1842 9.4818 92.1164 9.3195 91.9781 9.1813 C 91.8398 9.0432 91.6778 8.9753 91.4705 8.9753 C 91.2633 8.9753 91.101 9.0432 90.9628 9.1813 C 90.8246 9.3195 90.7568 9.4818 90.7568 9.6889 C 90.7568 9.8963 90.8246 10.0583 90.9628 10.1967 C 91.101 10.3348 91.2633 10.4025 91.4705 10.4025 Z M 91.0561 7.1702 H 91.9419 C 91.9624 6.8316 92.0166 6.5356 92.1131 6.2912 C 92.2477 5.9498 92.5957 5.5426 93.0834 5.0822 C 93.4337 4.7321 93.7022 4.4055 93.895 4.1038 C 94.0647 3.838 94.157 3.508 94.157 3.0982 C 94.157 2.4091 93.9117 1.9138 93.4339 1.5642 C 92.9239 1.1909 92.3163 0.9993 91.5964 0.9993 C 90.8595 0.9993 90.2949 1.192 89.8718 1.5497 C 89.5066 1.8584 89.2344 2.2127 89.0508 2.6148 L 89.8128 2.9151 C 89.9127 2.7157 90.0492 2.5161 90.2156 2.3166 C 90.543 1.9235 91.0268 1.755 91.5964 1.755 C 92.1041 1.755 92.5442 1.8952 92.8518 2.2315 C 93.1244 2.5298 93.2756 2.8776 93.2756 3.2662 C 93.2756 3.6183 93.1686 3.946 92.9647 4.2434 C 92.7788 4.5149 92.5485 4.7634 92.2773 4.9901 C 91.9761 5.257 91.7383 5.4872 91.5608 5.6811 C 91.3792 5.8795 91.2743 6.0255 91.2251 6.124 C 91.1405 6.2934 91.075 6.6286 91.0561 7.1702 Z';
export const contractInfoLabelSvgPath =
    'M 114.2421 9.8682 Z Z M 114.274 12.4495 C 113.3815 12.4495 112.5422 12.2801 111.7572 11.9413 C 110.9722 11.6025 110.2887 11.1427 109.7079 10.5619 C 109.1271 9.9811 108.6673 9.2981 108.3285 8.513 C 107.9897 7.7278 107.8203 6.8889 107.8203 5.9962 C 107.8203 5.1034 107.9897 4.2645 108.3285 3.4794 C 108.6673 2.6942 109.1271 2.0112 109.7079 1.4304 C 110.2887 0.8496 110.9722 0.3898 111.7572 0.051 C 112.5422 -0.2878 113.3815 -0.4572 114.274 -0.4572 C 115.1665 -0.4572 116.0058 -0.2878 116.7908 0.051 C 117.5758 0.3898 118.2593 0.8496 118.8401 1.4304 C 119.4209 2.0112 119.8807 2.6942 120.2195 3.4794 C 120.5583 4.2645 120.7277 5.1034 120.7277 5.9962 C 120.7277 6.8889 120.5583 7.7278 120.2195 8.513 C 119.8807 9.2981 119.4209 9.9811 118.8401 10.5619 C 118.2593 11.1427 117.5758 11.6025 116.7908 11.9413 C 116.0058 12.2801 115.1665 12.4495 114.274 12.4495 Z Z M 114.4705 10.4025 C 114.6778 10.4025 114.8398 10.3348 114.9781 10.1967 C 115.1164 10.0583 115.1842 9.8963 115.1842 9.6889 C 115.1842 9.4818 115.1164 9.3195 114.9781 9.1813 C 114.8398 9.0432 114.6778 8.9753 114.4705 8.9753 C 114.2633 8.9753 114.101 9.0432 113.9628 9.1813 C 113.8246 9.3195 113.7568 9.4818 113.7568 9.6889 C 113.7568 9.8963 113.8246 10.0583 113.9628 10.1967 C 114.101 10.3348 114.2633 10.4025 114.4705 10.4025 Z M 114.0561 7.1702 H 114.9419 C 114.9624 6.8316 115.0166 6.5356 115.1131 6.2912 C 115.2477 5.9498 115.5957 5.5426 116.0834 5.0822 C 116.4337 4.7321 116.7022 4.4055 116.895 4.1038 C 117.0647 3.838 117.157 3.508 117.157 3.0982 C 117.157 2.4091 116.9117 1.9138 116.4339 1.5642 C 115.9239 1.1909 115.3163 0.9993 114.5964 0.9993 C 113.8595 0.9993 113.2949 1.192 112.8718 1.5497 C 112.5066 1.8584 112.2344 2.2127 112.0508 2.6148 L 112.8128 2.9151 C 112.9127 2.7157 113.0492 2.5161 113.2156 2.3166 C 113.543 1.9235 114.0268 1.755 114.5964 1.755 C 115.1041 1.755 115.5442 1.8952 115.8518 2.2315 C 116.1244 2.5298 116.2756 2.8776 116.2756 3.2662 C 116.2756 3.6183 116.1686 3.946 115.9647 4.2434 C 115.7788 4.5149 115.5485 4.7634 115.2773 4.9901 C 114.9761 5.257 114.7383 5.4872 114.5608 5.6811 C 114.3792 5.8795 114.2743 6.0255 114.2251 6.124 C 114.1405 6.2934 114.075 6.6286 114.0561 7.1702 Z';
